import { ref, reactive } from '@vue/reactivity';
import type { Ref } from '@vue/reactivity';
import type { ToastNotification, ToastNotificationOptions, ToastAction } from '@/types';

// Global shared state for notifications - Single instance
const notifications: Ref<ToastNotification[]> = ref([]);
let notificationId: number = 0;

/**
 * Composable for managing toast notifications
 * Centralized system compatible with Vue.js 3 Composition API
 */
export function useNotifications() {
  
  /**
   * Add a notification
   * @param {Object} options - Notification options
   * @param {string} options.message - The message to display
   * @param {string} options.type - Notification type ('success', 'error', 'warning', 'info')
   * @param {number} options.duration - Display duration in ms (0 = permanent)
   * @param {boolean} options.closable - Whether the notification can be manually closed
   * @param {Object} options.action - Optional action with label and callback
   * @returns {number} ID of the created notification
   */
  const addNotification = (options: ToastNotificationOptions): number => {
    const {
      message,
      type = 'info',
      duration = 5000,
      closable = true,
      action = null,
      title = null
    } = options;

    const id = ++notificationId;
    const notification: ToastNotification = reactive({
      id,
      message,
      type,
      duration,
      closable,
      action,
      title: title || '',
      createdAt: Date.now(),
      isVisible: true
    });

    notifications.value.push(notification);

    // Auto-removal after specified duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }

    return id;
  };

  /**
   * Remove a notification by its ID
   * @param {number} id - ID of the notification to remove
   */
  const removeNotification = (id: number): void => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  /**
   * Remove all notifications
   */
  const clearAllNotifications = (): void => {
    notifications.value.splice(0);
  };

  /**
   * Convenience methods for different types of notifications
   */
  const notifySuccess = (message: string, options: Partial<ToastNotificationOptions> = {}): number => {
    return addNotification({
      message,
      type: 'success',
      ...options
    });
  };

  const notifyError = (message: string, options: Partial<ToastNotificationOptions> = {}): number => {
    return addNotification({
      message,
      type: 'error',
      duration: 0, // Errors stay until manually closed
      ...options
    });
  };

  const notifyWarning = (message: string, options: Partial<ToastNotificationOptions> = {}): number => {
    return addNotification({
      message,
      type: 'warning',
      duration: 7000, // Slightly longer for warnings
      ...options
    });
  };

  const notifyInfo = (message: string, options: Partial<ToastNotificationOptions> = {}): number => {
    return addNotification({
      message,
      type: 'info',
      ...options
    });
  };

  /**
   * Notification with action
   * @param {string} message - Notification message
   * @param {Object} action - Action with {label: string, callback: function}
   * @param {Object} options - Additional options
   */
  const notifyWithAction = (
    message: string, 
    action: ToastAction, 
    options: Partial<ToastNotificationOptions> = {}
  ): number => {
    return addNotification({
      message,
      action,
      duration: 0, // Notifications with actions don't auto-close
      ...options
    });
  };

  /**
   * Confirmation notification (with Yes/No actions)
   * @param {string} message - Confirmation message
   * @param {function} onConfirm - Confirmation callback
   * @param {function} onCancel - Cancellation callback (optional)
   * @param {Object} options - Additional options
   */
  const notifyConfirm = (
    message: string, 
    onConfirm: () => void, 
    onCancel: (() => void) | null = null, 
    options: Partial<ToastNotificationOptions> = {}
  ): number => {
    return addNotification({
      message,
      type: 'warning',
      duration: 0,
      action: {
        type: 'confirm',
        onConfirm,
        onCancel: onCancel || undefined
      },
      ...options
    });
  };

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    notifyWithAction,
    notifyConfirm
  };
}

// Note: No global instance exported to avoid reactivity conflicts
// Each component should use useNotifications() directly
